import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineArrowBack, MdLaunch } from 'react-icons/md';
import moment from 'moment';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import ButtonLink from 'components/ButtonLink';
import ScoreWithScale from 'components/ScoreWithScale2';
import { MULTIPLE_CHOICES } from 'global/constants';

import './index.css';

// 60 minutes for 40 questions.
const timePerQuestion = 60 * 60 / 40;

function DigitalBCAReport() {
    const [testData, setTestData] = useState(null);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);
    const { testId } = useParams();

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    useEffect(() => {
        axios.get(`tests/digital/bca/result/${testId}/`)
            .then(res => {
                setTestData(res.data);
            })
            .catch(error => {
                dispatch(setError(error));
            });
    }, []);

    if (!testData) return <></>;

    const showAnswer = organization.customizations.find(customization => customization === 'Enable Show Answer');
    const answers = testData.testModules.flatMap(testModule => testModule.answers);
    const totalTime = answers.reduce((total, answer) => total + answer.timeSpent, 0);

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    {testData.name} Report - {organization.name}
                </title>
            </Helmet>
            <div className='bcaReport__bar'>
                <button
                    className='bcaReport__back'
                    onClick={() => navigate('/tests')}
                >
                    <MdOutlineArrowBack className='bcaReport__backIcon' />
                    Back to My Tests
                </button>
            </div>
            <div className='bcaReport__headerGroup'>
                <div className='bcaReport__headerSection'>
                    <img
                        className='bcaReport__headerLogo'
                        alt='logo'
                        src={testData.logo}
                    />
                </div>
                <div className='bcaReport__headerSection'>
                    <div className='bcaReport__headerInfo'>
                        <p>
                            Name: {testData.studentName}
                        </p>
                        <p>
                            Test name: {testData.name}
                        </p>
                        <p>
                            Tested on: {moment(testData.testDate).format('MMMM D YYYY')}
                        </p>
                    </div>
                </div>
            </div>
            <div className='bcaReport__headerGroup'>
                <h1 className='bcaReport__headerTitle'>
                    Test Result Overview
                </h1>
                <div className='bcaReport__buttonGroup'>
                    {testData.reviewLink &&
                        <ButtonLink
                            className='bcaReport__button'
                            secondary
                            href={testData.reviewLink}
                            newTab
                        >
                            VIDEO REVIEW &nbsp; <MdLaunch />
                        </ButtonLink>
                    }
                    <ButtonLink className='bcaReport__button' to={`/review/digital-bca/${testId}`}>
                        REVIEW QUESTIONS
                    </ButtonLink>
                </div>
            </div>
            <div className='bcaReport__card'>
                <div className='bcaReport__cardRow'>
                    <div className='bcaReport__scoresGroup'>
                        <h2 className='bcaReport__scoreTitle'>TOTAL SCORE</h2>
                        {/* +2 per right answer, -0.5 per wrong one: keep the half point and the sign. */}
                        <ScoreWithScale
                            className='bcaReport__scoreWithScale'
                            score={Number(testData.score)}
                            scoreMin={Number(testData.minScore)}
                            scoreMax={Number(testData.maxScore)}
                            fontSize={70}
                        />
                    </div>
                    <div className='bcaReport__overview'>
                        <h2 className='bcaReport__cardSubTitle'>
                            Raw Scores
                        </h2>
                        <div className='bcaReport__overviewRow'>
                            <div className='bcaReport__overviewItem'>
                                <table className='bcaReport__overviewTable'>
                                    <tbody>
                                        <tr>
                                            <th>Correct</th>
                                            <th>Incorrect</th>
                                            <th>Omitted</th>
                                            <th>Time</th>
                                        </tr>
                                        <tr>
                                            <td>{testData.correct}/{answers.length}</td>
                                            <td>{testData.wrong}</td>
                                            <td>{testData.blank}</td>
                                            <td>{formatTime(totalTime)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <p className='bcaReport__scoringNote'>
                            Each correct answer earns 2 points, each incorrect answer loses 0.5 point, and an omitted question earns 0 points.
                        </p>
                    </div>
                </div>
            </div>
            <div className='bcaReport__questions'>
                <h2 className='bcaReport__questionsHeader'>
                    Questions Overview
                </h2>
                <div className='bcaReport__questionsOverview'>
                    <div className='bcaReport__questionsSummary'>
                        <div className='bcaReport__questionsSummaryItem'>
                            <p className='bcaReport__questionSummaryNumber'>{answers.length}</p>
                            <p className='bcaReport__questionSummaryText'>Total Questions</p>
                        </div>
                        <div className='bcaReport__questionsSummaryItem'>
                            <p className='bcaReport__questionSummaryNumber'>{testData.correct}</p>
                            <p className='bcaReport__questionSummaryText'>Correct Answers</p>
                        </div>
                        <div className='bcaReport__questionsSummaryItem'>
                            <p className='bcaReport__questionSummaryNumber'>{testData.wrong}</p>
                            <p className='bcaReport__questionSummaryText'>Incorrect Answers</p>
                        </div>
                        <div className='bcaReport__questionsSummaryItem'>
                            <p className='bcaReport__questionSummaryNumber'>{testData.blank}</p>
                            <p className='bcaReport__questionSummaryText'>Omitted</p>
                        </div>
                    </div>
                    <table className='bcaReport__questionsTable'>
                        <tbody>
                            <tr>
                                <th>Question</th>
                                {showAnswer && <th>Correct Answer</th>}
                                <th>Your Answer</th>
                                <th>Time Spent</th>
                            </tr>
                            {answers.map(answer => (
                                <tr key={answer.questionNumber}>
                                    <td>{answer.questionNumber}</td>
                                    {showAnswer && <td>{MULTIPLE_CHOICES[answer.correctChoice]}</td>}
                                    <td>{answer.isBlank
                                        ?   <p className='bcaReport__questionOmitted'>Omitted</p>
                                        :   answer.isCorrect
                                        ?   <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                        :   <p className='bcaReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                    }</td>
                                    <td>
                                        <p className={answer.timeSpent > timePerQuestion * 1.5
                                            ?   'bcaReport__timeSpentBad'
                                            :   answer.timeSpent > timePerQuestion
                                            ?   'bcaReport__timeSpentWarning'
                                            :   'bcaReport__timeSpentGood'
                                        }>
                                            {formatTime(answer.timeSpent)}
                                        </p>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default DigitalBCAReport;
